import { useState } from 'react';
import { updatePassword } from 'aws-amplify/auth';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Container,
  TextField,
  Button,
  Alert,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { useAppContext } from '../../../utils/content.util';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { onError } from '../../../utils/error.util';
import { checkPasswordRequirement, validatePassword } from '../../../utils/password.util';

export default function ProfileContainer() {
  const { currentUser } = useAppContext();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  if (!currentUser) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography variant="h5">No user information available</Typography>
      </Container>
    );
  }

  // Handle permissions - could be array or string
  const permissionsValue = currentUser['custom:permissions'] as string[] | string | undefined;
  const permissions = !permissionsValue
    ? []
    : Array.isArray(permissionsValue)
    ? permissionsValue
    : typeof permissionsValue === 'string'
    ? permissionsValue.split(',').filter((p: string) => p.trim())
    : [];

  function getErrorMessage(error: any): string {
    const errorMessage = error?.message || error?.toString() || '';
    const errorCode = error?.name || error?.code || '';

    if (errorCode === 'NotAuthorizedException' || errorMessage.includes('Incorrect username or password')) {
      return 'Current password is incorrect';
    }
    if (errorCode === 'InvalidPasswordException' || errorMessage.includes('Password did not conform')) {
      return 'Password does not meet requirements. Please check the password requirements below.';
    }
    if (errorCode === 'LimitExceededException' || errorMessage.includes('Too many attempts')) {
      return 'Too many attempts. Please try again later.';
    }
    if (errorMessage.includes('Network') || errorMessage.includes('Failed to fetch')) {
      return 'Network error. Please check your internet connection and try again.';
    }

    if (errorMessage && !errorMessage.includes('AWS') && !errorMessage.includes('Cognito')) {
      return errorMessage;
    }

    return 'An error occurred. Please try again.';
  }

  async function handlePasswordUpdate(event: React.FormEvent) {
    event.preventDefault();

    setIsLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    // Validate current password
    if (!currentPassword) {
      setErrorMessage('Please enter your current password');
      setIsLoading(false);
      return;
    }

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setErrorMessage('New passwords do not match');
      setIsLoading(false);
      return;
    }

    // Validate password requirements
    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      setErrorMessage(passwordError);
      setIsLoading(false);
      return;
    }

    // Validate new password is different from current
    if (currentPassword === newPassword) {
      setErrorMessage('New password must be different from current password');
      setIsLoading(false);
      return;
    }

    try {
      await updatePassword({ oldPassword: currentPassword, newPassword });
      setSuccessMessage('Password updated successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (e: any) {
      onError(e);
      setErrorMessage(getErrorMessage(e));
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Profile
        </Typography>
        <Typography variant="body1" color="text.secondary">
          View your account information
        </Typography>
      </Box>

      <Paper elevation={2} sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <PersonIcon sx={{ mr: 2, color: 'primary.main' }} />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  First Name
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {currentUser.given_name || 'Not set'}
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <PersonIcon sx={{ mr: 2, color: 'primary.main' }} />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Last Name
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {currentUser.family_name || 'Not set'}
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <EmailIcon sx={{ mr: 2, color: 'primary.main' }} />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Email
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {currentUser.email || 'Not set'}
                </Typography>
              </Box>
            </Box>
          </Grid>

          {permissions.length > 0 && (
            <Grid size={{ xs: 12 }}>
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Permissions
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {permissions.map((permission, index) => (
                    <Typography
                      key={index}
                      variant="body2"
                      sx={{
                        px: 1.5,
                        py: 0.5,
                        bgcolor: 'primary.light',
                        color: 'primary.contrastText',
                        borderRadius: 1,
                        fontSize: '0.75rem',
                      }}
                    >
                      {permission}
                    </Typography>
                  ))}
                </Box>
              </Box>
            </Grid>
          )}
        </Grid>
      </Paper>

      <Paper elevation={2} sx={{ p: 3, mt: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <LockIcon sx={{ mr: 2, color: 'primary.main' }} />
            <Typography variant="h6">Update Password</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Change your account password
          </Typography>
        </Box>

        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage('')}>
            {errorMessage}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
            {successMessage}
          </Alert>
        )}

        <form onSubmit={handlePasswordUpdate}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                required
                id="currentPassword"
                name="currentPassword"
                type={showCurrentPassword ? 'text' : 'password'}
                label="Current Password"
                autoComplete="current-password"
                value={currentPassword}
                onChange={(e) => {
                  setCurrentPassword(e.target.value);
                  if (errorMessage) setErrorMessage('');
                }}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                          edge="end"
                          aria-label="toggle password visibility"
                        >
                          {showCurrentPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                required
                id="newPassword"
                name="newPassword"
                type={showNewPassword ? 'text' : 'password'}
                label="New Password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  if (errorMessage) setErrorMessage('');
                }}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          edge="end"
                          aria-label="toggle password visibility"
                        >
                          {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
              {newPassword && (
                <Box sx={{ mt: 2, mb: 1 }}>
                  <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'text.secondary' }}>
                    Password requirements:
                  </Typography>
                  <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      {checkPasswordRequirement(newPassword, 'length') ? (
                        <CheckCircleIcon sx={{ fontSize: '14px', color: 'success.main', mr: 0.5 }} />
                      ) : (
                        <CancelIcon sx={{ fontSize: '14px', color: 'error.main', mr: 0.5 }} />
                      )}
                      <span>At least 8 characters</span>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      {checkPasswordRequirement(newPassword, 'uppercase') ? (
                        <CheckCircleIcon sx={{ fontSize: '14px', color: 'success.main', mr: 0.5 }} />
                      ) : (
                        <CancelIcon sx={{ fontSize: '14px', color: 'error.main', mr: 0.5 }} />
                      )}
                      <span>At least one uppercase letter</span>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      {checkPasswordRequirement(newPassword, 'lowercase') ? (
                        <CheckCircleIcon sx={{ fontSize: '14px', color: 'success.main', mr: 0.5 }} />
                      ) : (
                        <CancelIcon sx={{ fontSize: '14px', color: 'error.main', mr: 0.5 }} />
                      )}
                      <span>At least one lowercase letter</span>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      {checkPasswordRequirement(newPassword, 'number') ? (
                        <CheckCircleIcon sx={{ fontSize: '14px', color: 'success.main', mr: 0.5 }} />
                      ) : (
                        <CancelIcon sx={{ fontSize: '14px', color: 'error.main', mr: 0.5 }} />
                      )}
                      <span>At least one number</span>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      {checkPasswordRequirement(newPassword, 'special') ? (
                        <CheckCircleIcon sx={{ fontSize: '14px', color: 'success.main', mr: 0.5 }} />
                      ) : (
                        <CancelIcon sx={{ fontSize: '14px', color: 'error.main', mr: 0.5 }} />
                      )}
                      <span>At least one special character</span>
                    </Box>
                  </Box>
                </Box>
              )}
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                required
                id="confirmPassword"
                name="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                label="Confirm New Password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  if (errorMessage) setErrorMessage('');
                }}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                          aria-label="toggle password visibility"
                        >
                          {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
              {confirmPassword && (
                <Box sx={{ mt: 1 }}>
                  {newPassword === confirmPassword ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', fontSize: '0.75rem', color: 'success.main' }}>
                      <CheckCircleIcon sx={{ fontSize: '14px', mr: 0.5 }} />
                      <span>Passwords match</span>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', fontSize: '0.75rem', color: 'error.main' }}>
                      <CancelIcon sx={{ fontSize: '14px', mr: 0.5 }} />
                      <span>Passwords do not match</span>
                    </Box>
                  )}
                </Box>
              )}
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={!currentPassword || !newPassword || !confirmPassword || isLoading}
                sx={{ mt: 2 }}
              >
                {isLoading ? 'Updating...' : 'Update Password'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Container>
  );
}
